/**
 * Cross-Sell Recommendations with Swiper
 * Stale-while-revalidate: shows cached slides instantly, then refreshes in background.
 * Avoids extra /cart.js fetch by tracking product_id via cart:change events.
 */

const _CS_CACHE_PREFIX = 'cross-sell-v1-';
const _CS_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

let _csPid = null;
try { _csPid = localStorage.getItem('cross-sell-pid'); } catch (e) {}

document.addEventListener('cart:change', (event) => {
  const items = event.detail?.cart?.items;
  if (items?.length > 0) {
    _csPid = String(items[0].product_id);
    try { localStorage.setItem('cross-sell-pid', _csPid); } catch (e) {}
  } else if (items !== undefined && items.length === 0) {
    _csPid = null;
    try { localStorage.removeItem('cross-sell-pid'); } catch (e) {}
  }
});

function _csGetCache(productId) {
  try {
    const raw = localStorage.getItem(_CS_CACHE_PREFIX + productId);
    if (!raw) return null;
    const { slides, ts } = JSON.parse(raw);
    if (Date.now() - ts > _CS_CACHE_TTL) {
      localStorage.removeItem(_CS_CACHE_PREFIX + productId);
      return null;
    }
    return slides; // string[]
  } catch (e) {
    return null;
  }
}

function _csSetCache(productId, slides) {
  try {
    localStorage.setItem(
      _CS_CACHE_PREFIX + productId,
      JSON.stringify({ slides, ts: Date.now() })
    );
  } catch (e) {}
}

if (!customElements.get('cross-sell-recommendations')) {
  customElements.define('cross-sell-recommendations', class extends HTMLElement {
    connectedCallback() {
      this.limit = this.getAttribute('data-limit') || 10;
      this.sectionId = 'api-cross-sell-recommendations';
      this.swiperInstance = null;
      this._loading = false;
      this.loadRecommendations();
    }

    disconnectedCallback() {
      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
      }
    }

    async loadRecommendations() {
      if (this._loading) return;
      this._loading = true;

      try {
        let productId = _csPid;

        if (!productId) {
          const cart = await fetch('/cart.js').then(r => r.json());
          if (!cart.items || cart.items.length === 0) {
            this.style.display = 'none';
            return;
          }
          productId = String(cart.items[0].product_id);
          _csPid = productId;
          try { localStorage.setItem('cross-sell-pid', productId); } catch (e) {}
        }

        const cachedSlides = _csGetCache(productId);
        let renderedFromCache = false;

        if (cachedSlides?.length > 0) {
          this._renderSlides(cachedSlides);
          renderedFromCache = true;
        }

        const url = `/recommendations/products?product_id=${productId}&intent=related&limit=${this.limit}&section_id=${this.sectionId}`;
        const response = await fetch(url);

        if (!response.ok) {
          if (!renderedFromCache) this.style.display = 'none';
          return;
        }

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const container = doc.querySelector('.cross-sell-recommendations');

        if (!container || container.children.length === 0) {
          if (!renderedFromCache) this.style.display = 'none';
          return;
        }

        const freshSlides = Array.from(container.children).map(el => el.outerHTML);

        _csSetCache(productId, freshSlides);

        const cachedStr = cachedSlides ? cachedSlides.join('') : null;
        const freshStr = freshSlides.join('');

        if (!renderedFromCache || cachedStr !== freshStr) {
          this._renderSlides(freshSlides);
        }

      } catch (error) {
        console.error('[CrossSell] Error:', error);
        if (!this.querySelector('.swiper-slide')) {
          this.style.display = 'none';
        }
      } finally {
        this._loading = false;
      }
    }

    _renderSlides(slides) {
      const wrapper = this.querySelector('.swiper-wrapper');
      if (!wrapper || slides.length === 0) {
        this.style.display = 'none';
        return;
      }

      wrapper.innerHTML = '';
      slides.forEach(slideHtml => {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        slide.innerHTML = slideHtml;
        wrapper.appendChild(slide);
      });

      this.style.display = '';
      this.initSwiper();
    }

    initSwiper() {
      if (typeof Swiper === 'undefined') {
        console.warn('[CrossSell] Swiper not loaded');
        return;
      }

      const swiperEl = this.querySelector('.cross-sell-swiper');
      if (!swiperEl) return;

      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
      }

      this.swiperInstance = new Swiper(swiperEl, {
        slidesPerView: 1.8,
        spaceBetween: 12,
        grabCursor: true,
        navigation: {
          nextEl: '.cross-sell-swiper__button-next',
          prevEl: '.cross-sell-swiper__button-prev',
        },
        breakpoints: {
          640: {
            slidesPerView: 2.9,
            spaceBetween: 12
          }
        }
      });
    }
  });
}

// Initialize static cross-sell Swiper sliders
function initStaticCrossSellSwipers() {
  if (typeof Swiper === 'undefined') {
    setTimeout(initStaticCrossSellSwipers, 100);
    return;
  }

  document.querySelectorAll('.cross-sell-swiper:not(.swiper-initialized)').forEach(swiperEl => {
    // Skip if inside cross-sell-recommendations custom element
    if (swiperEl.closest('cross-sell-recommendations')) return;

    new Swiper(swiperEl, {
      slidesPerView: 1.8,
      spaceBetween: 12,
      grabCursor: true,
      watchOverflow: true,
      navigation: {
        nextEl: '.cross-sell-swiper__button-next',
        prevEl: '.cross-sell-swiper__button-prev',
      },
      breakpoints: {
        640: {
          slidesPerView: 2.9,
          spaceBetween: 12
        }
      }
    });
  });
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', initStaticCrossSellSwipers);

// Re-initialize when cart drawer is refreshed
document.addEventListener('cart-drawer:refreshed', () => {
  // Small delay to ensure DOM is updated
  setTimeout(initStaticCrossSellSwipers, 50);
});
